'use client';

import React, { useCallback, useMemo } from 'react';

import Box, { BoxProps } from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import JsonView, { JsonViewProps } from '@uiw/react-json-view';

import { ErrorMessage } from './errorMessage';
import { CopyToClipboardButton } from '../controls/copyToClipboard';
import { parseNestedJsonStrings } from '../utils/parseNestedJsonString';

interface Props {
  collapsed?: boolean | number;
  errorMessage?: string;
  fallback?: string;
  highlight?: {
    [key: string]: string;
  };
  indent?: number;
  json: string | Record<string, any>;
  jsonOptions?: Omit<JsonViewProps<object>, 'src' | 'indentWidth' | 'name' | 'theme' | 'ref'>;
  sx?: BoxProps['sx'];
}

export function JSONBlob({
  collapsed = 1,
  errorMessage,
  fallback,
  highlight = {},
  indent = 4,
  json,
  jsonOptions = {},
  sx,
}: Props) {
  const { palette } = useTheme();
  const hasHighlight = useMemo(() => Object.keys(highlight).length > 0, [highlight]);

  const parsedJson = useMemo(() => {
    try {
      const parsed = typeof json === 'string' ? JSON.parse(json) : json;

      return parseNestedJsonStrings(parsed) as object;
    } catch {
      return {
        error: fallback ?? 'Invalid JSON',
      };
    }
  }, [json, fallback]);

  const code = useMemo(() => {
    try {
      return typeof json === 'string'
        ? JSON.stringify(JSON.parse(json), undefined, indent)
        : JSON.stringify(json, undefined, indent);
    } catch {
      return fallback || '';
    }
  }, [json, fallback, indent]);

  // Define the style object with CSS variables here
  const jsonViewStyles = {
    // Expansion lines (from base02)
    '--w-rjv-arrow-color': palette.text.primary,

    '--w-rjv-background-color': 'rgba(0, 0, 0, 0)',

    // Colons (from base01)
    '--w-rjv-brackets-color': palette.text.secondary,

    // Braces (from base01)
    '--w-rjv-colon-color': palette.text.secondary,

    // Use intended transparent background
    '--w-rjv-color': palette.text.primary,

    // Default update color
    '--w-rjv-copied-color': palette.text.primary,

    // Default copied color
    '--w-rjv-copied-success-color': palette.success?.main || '#28a745',

    // Success color
    '--w-rjv-curlybraces-color': palette.text.secondary,

    // Item count (from base04)
    '--w-rjv-edit-color': palette.text.primary,

    // Brackets (from base01)
    '--w-rjv-ellipsis-color': palette.text.secondary,

    '--w-rjv-font-family': 'monospace',

    // Expanded/active arrow (from base0D) - might need inactive state adjustment
    '--w-rjv-info-color': palette.text.primary,

    // String keys (from base07)
    '--w-rjv-key-number': palette.text.primary,

    // General text (from base05/06/08)
    '--w-rjv-key-string': palette.info.main,

    // Number keys / array indexes (from base0C)
    '--w-rjv-line-color': 'transparent',

    // Ellipsis (sensible default)
    '--w-rjv-quotes-color': palette.info.main,

    // Quotes around keys (from base07)
    '--w-rjv-quotes-string-color': palette.warning.main,

    // Float values (from base0F)
    '--w-rjv-type-bigint-color': '#E663F1',

    // BigInt values (from base0B)
    '--w-rjv-type-boolean-color': '#4886FF',

    // Boolean values (from base0E)
    '--w-rjv-type-date-color': palette.warning.main,

    // Int values (from base0F)
    '--w-rjv-type-float-color': '#E663F1',

    // String values (from base09)
    '--w-rjv-type-int-color': '#E663F1',

    // Null values (text color from base0A adjusted)
    '--w-rjv-type-nan-color': palette.error?.main || '#859900',

    // URL values (treat as links/keys)
    '--w-rjv-type-null-color': palette.text.secondary,

    // Quotes around strings (from base09)
    '--w-rjv-type-string-color': palette.warning.main,

    // NaN values
    '--w-rjv-type-undefined-color': palette.text.secondary,

    // Date values (treat as strings)
    '--w-rjv-type-url-color': palette.info.main,

    // Default edit color
    '--w-rjv-update-color': palette.text.primary,

    fontSize: '12px',

    lineHeight: '1.8', // Undefined values (text color from base0A adjusted)
  } as React.CSSProperties; // Cast to CSSProperties

  const renderHighlights = useCallback(
    (
      {
        children,
        style,
      }: {
        [key: string]: any;
        children?: React.ReactNode;
        style?: React.CSSProperties;
      },
      {
        keyName,
        type: _type,
        value,
      }: {
        keyName: string | number;
        type: 'value' | 'type';
        value?: unknown;
      },
    ) => {
      const highlightStyle = { ...style, padding: '2px' };
      try {
        if (
          highlight[String(keyName)] !== undefined &&
          highlight[String(keyName)] === String(value)
        ) {
          highlightStyle.backgroundColor = '#3A568B';
        }

        return <span style={highlightStyle}>{children}</span>;
      } catch {
        return <span style={highlightStyle}>{children}</span>;
      }
    },
    [highlight],
  );

  if (errorMessage) {
    return <ErrorMessage message={errorMessage} />;
  }

  return (
    <Box
      sx={{
        alignItems: 'start',
        display: 'flex',
        justifyContent: 'space-between',
        position: 'relative',
        width: '100%',
        ...sx,
      }}
    >
      {document && (
        <JsonView
          collapsed={collapsed}
          displayDataTypes={false}
          enableClipboard={false}
          indentWidth={indent}
          shortenTextAfterLength={0}
          style={jsonViewStyles}
          value={parsedJson}
          {...jsonOptions}
        >
          {hasHighlight && (
            <>
              <JsonView.String render={renderHighlights} />
              <JsonView.Bigint render={renderHighlights} />
              <JsonView.Float render={renderHighlights} />
              <JsonView.Int render={renderHighlights} />
              <JsonView.Url render={renderHighlights} />
            </>
          )}
        </JsonView>
      )}
      <Box position="sticky" right={0} top={0}>
        <CopyToClipboardButton text={code} />
      </Box>
    </Box>
  );
}
